import { useState } from 'react';
import { Icon } from '../../ui/Icon/Icon';
import { DeleteConfirmationModal, deleteConfirmTitle } from '../../ui/DeleteConfirmationModal/DeleteConfirmationModal';
import { buildDisplayMediaSrc } from '../../../../../services/displayApi';
import type { MediaLibraryItem } from '../../../data/types';
import './MediaCard.css';

interface MediaCardProps {
  item: MediaLibraryItem;
  selected?: boolean;
  /** Only wired up when the card is clickable — e.g. the playlist/wizard media picker. Omit to make the card non-interactive (Media Library grid). */
  onPreview?: () => void;
  onDelete?: () => void;
  /** Selection mode: shows a checkmark badge instead of the delete control (used by playlist/wizard media pickers). */
  checkable?: boolean;
}

const T3 = 'var(--t3, #9CA3B8)';

function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`;
  return `${bytes} B`;
}

export function MediaCard({ item, selected, onPreview, onDelete, checkable }: MediaCardProps) {
  const [failed, setFailed] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isVideo = item.kind === 'video';
  const mediaSrc = buildDisplayMediaSrc(item.id, item.mimeType) ?? item.url;

  const rootClasses = [
    'media-card-root',
    onPreview ? 'is-clickable' : '',
    hovered ? 'is-hovered' : '',
    selected ? 'is-selected' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      onClick={onPreview}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={rootClasses}
    >
      <div className="media-card-thumb-area">
        {!failed ? (
          isVideo ? (
            <video
              src={mediaSrc}
              muted
              preload="metadata"
              className="media-card-media-el"
              onError={() => setFailed(true)}
            />
          ) : (
            <img
              src={mediaSrc}
              alt={item.name}
              className="media-card-media-el"
              onError={() => setFailed(true)}
            />
          )
        ) : (
          <Icon name={isVideo ? 'videocam' : 'image'} size={28} color={T3} />
        )}
        <div className="media-card-gradient-overlay" />
        <div className="media-card-kind-badge">
          <Icon name={isVideo ? 'videocam' : 'image'} size={13} color="#fff" />
        </div>
        {checkable && (
          <div className={`media-card-check-badge ${selected ? 'is-selected' : ''}`}>
            {selected && <Icon name="check" size={11} color="#fff" />}
          </div>
        )}
      </div>
      <div className="media-card-body">
        <div className="media-card-title" title={item.name}>
          {item.name}
        </div>
        <div className="media-card-size">{formatBytes(item.sizeBytes)}</div>
        {!checkable && onDelete && (
          <div onClick={(e) => e.stopPropagation()} className="media-card-actions">
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="media-card-delete-btn"
            >
              <Icon name="delete" size={14} color="var(--red, #DC2626)" />
              Delete
            </button>
          </div>
        )}
      </div>

      {onDelete && (
        <DeleteConfirmationModal
          open={confirmingDelete}
          onClose={() => setConfirmingDelete(false)}
          onConfirm={() => { setConfirmingDelete(false); onDelete(); }}
          title={deleteConfirmTitle(item.name)}
          description="This media will be permanently removed from your library. This action cannot be undone."
        />
      )}
    </div>
  );
}
